import { Button } from "@medme/ui"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

type LeadsHeaderProps = {
	total?: number
	newCount?: number
	isSummaryLoading: boolean
	isSummaryError: boolean
	onRetrySummary: () => void
}

export function LeadsHeader({
	total,
	newCount,
	isSummaryLoading,
	isSummaryError,
	onRetrySummary,
}: LeadsHeaderProps) {
	const { t } = useTranslation()
	return (
		<header className="flex flex-col justify-between gap-5 xl:flex-row xl:items-end">
			<div>
				<h1 className="text-3xl font-semibold tracking-tight">
					{t("leads.title")}
				</h1>
				<p className="mt-1 text-base text-mm-muted-fg">
					{t("leads.description")}
				</p>
			</div>
			<div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
				<div
					className="rounded-xl border bg-mm-card px-4 py-3"
					aria-busy={isSummaryLoading}
				>
					<div className="text-xl font-semibold">
						{isSummaryLoading ? (
							<Loader2 className="size-5 animate-spin" aria-hidden="true" />
						) : (
							(total ?? "—")
						)}
					</div>
					<div className="text-xs text-mm-muted-fg">
						{t("leads.summary.total")}
					</div>
				</div>
				<div
					className="rounded-xl border bg-mm-card px-4 py-3"
					aria-busy={isSummaryLoading}
				>
					<div className="text-xl font-semibold">
						{isSummaryLoading ? (
							<Loader2 className="size-5 animate-spin" aria-hidden="true" />
						) : (
							(newCount ?? "—")
						)}
					</div>
					<div className="text-xs text-mm-muted-fg">
						{t("leads.summary.new")}
					</div>
				</div>
				{isSummaryLoading && (
					<span className="sr-only">{t("leads.summary.loading")}</span>
				)}
				{isSummaryError && (
					<div
						role="alert"
						className="col-span-2 flex items-center justify-between gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm sm:order-last sm:basis-full"
					>
						<span>{t("leads.summary.error")}</span>
						<Button
							className="h-auto shrink-0 py-1.5"
							size="sm"
							variant="outline"
							disabled={isSummaryLoading}
							onClick={onRetrySummary}
						>
							{t("leads.actions.retry")}
						</Button>
					</div>
				)}
			</div>
		</header>
	)
}
